/**
 * Types and utility functions for ReviewQuestionnaire.
 */
import type { FilledDataset, FilledDatasetValue } from '../../lib/api';

export interface ChoiceOption {
  value: string;
  label: string;
  score?: number;
}

export interface ConditionalLogic {
  show_if?: {
    question_id: number;
    operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
    value?: string | boolean | number;
  }[];
  logic?: 'and' | 'or';
}

export interface Question {
  id: number;
  question_text: string;
  help_text?: string;
  question_type: string;
  is_required: boolean;
  sort_order: number;
  choices?: Array<ChoiceOption | string>;
  max_score?: number;
  conditional_logic?: ConditionalLogic;
}

export interface Section {
  id: number;
  title: string;
  description?: string;
  sort_order: number;
  questions: Question[];
  conditional_logic?: ConditionalLogic;
}

export interface DatasetDetail {
  id: number;
  name: string;
  description?: string;
  dataset_type: string;
  sections: Section[];
}

export interface FilledDatasetDetail extends FilledDataset {
  dataset_detail?: DatasetDetail;
  values?: FilledDatasetValue[];
  supplier_name?: string;
  customer_name?: string;
}

export interface ApprovalStatusData {
  total_levels: number;
  current_level: number | null;
  completed_levels: number;
  levels: Array<{
    level: number;
    decision: string;
    reviewer: string | null;
    decided_at: string | null;
    comments: string;
  }>;
  is_complete: boolean;
  is_rejected: boolean;
}

export const statusColors: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-purple-100 text-purple-800',
  more_info_required: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

/** Normalize choices from string or object format. */
export const normalizeChoices = (choices?: Array<ChoiceOption | string>): ChoiceOption[] => {
  if (!choices) return [];
  return choices.map((choice) => {
    if (typeof choice === 'string') {
      return { value: choice, label: choice };
    }
    return choice;
  });
};

/** Format a filled answer value for display. */
export const formatAnswer = (question: Question, answer: FilledDatasetValue | undefined): string => {
  if (!answer) return 'Not answered';

  switch (question.question_type) {
    case 'text':
    case 'textarea':
      return answer.text_value || 'Not answered';
    case 'number':
      return answer.number_value?.toString() || 'Not answered';
    case 'date':
      return answer.date_value || 'Not answered';
    case 'yes_no':
      if (answer.boolean_value === true) return 'Yes';
      if (answer.boolean_value === false) return 'No';
      return answer.text_value || 'Not answered';
    case 'single_choice':
      if (answer.choice_values?.length) {
        const choices = normalizeChoices(question.choices);
        const selected = choices.find(c => c.value === answer.choice_values![0]);
        return selected?.label || answer.choice_values[0];
      }
      return 'Not answered';
    case 'multiple_choice':
      if (answer.choice_values?.length) {
        const choices = normalizeChoices(question.choices);
        return answer.choice_values
          .map(v => choices.find(c => c.value === v)?.label || v)
          .join(', ');
      }
      return 'Not answered';
    case 'rating':
      return answer.number_value?.toString() || 'Not answered';
    default:
      return answer.text_value || 'Not answered';
  }
};
